import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BlockType, ListStyle } from '../../config';
import { isSelectionSubscript, toggleSubscript } from '../../subscript';
import './selection-toolbar.scss';

// =======================================
// INTERFACES
// =======================================

interface SelectionToolbarProps {
  isEditing: boolean;
  onBlockTypeChange?: (type: BlockType, level?: number, blockIndex?: number, listStyle?: ListStyle) => void;
  getBlockInfo?: (index: number) => {
    type: BlockType;
    headingLevel?: 1 | 2 | 3;
    listStyle?: ListStyle;
    spacing?: { above: boolean; below: boolean };
  } | null;
  // Toggle the blank line above or below the selected block
  onSpacingToggle?: (side: 'above' | 'below', blockIndex: number) => void;
  /**
   * The canvas this toolbar belongs to.
   *
   * Every mounted toolbar listens to the same document-level selectionchange,
   * so without this they all answer at once: a page of instruction items --
   * each holding a nested canvas -- lit up one toolbar per item. It matters for
   * a plain multi-page document too, where every page's canvas has one.
   */
  canvasRef?: React.RefObject<HTMLDivElement | null>;
}

interface ToolbarPosition {
  top: number;
  left: number;
  visible: boolean;
}

// =======================================
// BLOCK TYPE OPTIONS
// =======================================

const BLOCK_TYPE_OPTIONS: { type: BlockType; label: string; icon: string; level?: number; listStyle?: ListStyle }[] = [
  { type: 'paragraph', label: 'Text', icon: 'T' },
  { type: 'heading', label: 'Heading 1', icon: 'H₁', level: 1 },
  { type: 'heading', label: 'Heading 2', icon: 'H₂', level: 2 },
  { type: 'heading', label: 'Heading 3', icon: 'H₃', level: 3 },
  // One list block, three markers: switching between them keeps the items.
  { type: 'bullet_list', label: 'Bulleted list', icon: '•', listStyle: 'bullet' },
  { type: 'bullet_list', label: 'Numbered list', icon: '1.', listStyle: 'number' },
  { type: 'bullet_list', label: 'Lettered list', icon: 'a.', listStyle: 'letter' },
  { type: 'scope_list', label: 'To-do list', icon: '☐' },
];

// =======================================
// MAIN COMPONENT
// =======================================

// Highlight color options
const HIGHLIGHT_COLORS = [
  { name: 'Yellow', color: '#fef08a' },
  { name: 'Green', color: '#bbf7d0' },
  { name: 'Blue', color: '#bfdbfe' },
  { name: 'Pink', color: '#fbcfe8' },
  { name: 'Orange', color: '#fed7aa' },
  { name: 'Purple', color: '#ddd6fe' },
  { name: 'None', color: 'transparent' },
];

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  isEditing,
  onBlockTypeChange,
  getBlockInfo,
  onSpacingToggle,
  canvasRef
}) => {
  const [position, setPosition] = useState<ToolbarPosition>({ top: 0, left: 0, visible: false });
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [showHighlightMenu, setShowHighlightMenu] = useState(false);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState<number | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const blockMenuRef = useRef<HTMLDivElement>(null);
  const highlightMenuRef = useRef<HTMLDivElement>(null);

  // Get current block info from the selected block
  const blockInfo = selectedBlockIndex !== null ? getBlockInfo?.(selectedBlockIndex) : null;
  const currentBlockType = blockInfo?.type;
  const currentHeadingLevel = blockInfo?.headingLevel;
  const currentListStyle = blockInfo?.listStyle;
  const currentSpacing = blockInfo?.spacing ?? { above: true, below: true };

  // Check current formatting state
  const [formatting, setFormatting] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    subscript: false,
    code: false
  });

  // Update toolbar position based on selection
  const updateToolbarPosition = useCallback(() => {
    const selection = window.getSelection();
    
    if (!selection || selection.isCollapsed || !isEditing) {
      setPosition(prev => ({ ...prev, visible: false }));
      setShowBlockMenu(false);
      return;
    }

    // Check if selection is within a contenteditable
    const anchorNode = selection.anchorNode;
    const isInEditable = anchorNode?.parentElement?.closest('[contenteditable="true"]');
    
    if (!isInEditable) {
      setPosition(prev => ({ ...prev, visible: false }));
      return;
    }

    // ...and in THIS canvas. Identity against the NEAREST enclosing canvas,
    // not containment: an instruction item's canvas is nested inside the
    // page's, so the page's would answer for a selection inside an item too.
    if (canvasRef?.current && isInEditable.closest('.block-canvas') !== canvasRef.current) {
      setPosition(prev => ({ ...prev, visible: false }));
      setShowBlockMenu(false);
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    
    // Get the block wrapper for positioning
    const blockWrapper = isInEditable.closest('.block-wrapper');
    const blockContent = blockWrapper?.querySelector('.block-content');
    const canvas = isInEditable.closest('.block-canvas');
    const canvasRect = canvas?.getBoundingClientRect();
    const blockContentRect = blockContent?.getBoundingClientRect();
    
    if (!canvasRect || !blockContentRect || !blockWrapper) {
      setPosition(prev => ({ ...prev, visible: false }));
      return;
    }
    
    // Determine block index from DOM
    const allBlockWrappers = canvas?.querySelectorAll('.block-wrapper');
    let blockIndex: number | null = null;
    if (allBlockWrappers) {
      allBlockWrappers.forEach((wrapper, index) => {
        if (wrapper === blockWrapper) {
          blockIndex = index;
        }
      });
    }
    setSelectedBlockIndex(blockIndex);
    
    // Position toolbar above selection, left-aligned with block content
    setPosition({
      top: rect.top - canvasRect.top - 44, // 44px above the selection
      left: blockContentRect.left - canvasRect.left, // Align with block start
      visible: true
    });

    // Check current formatting
    setFormatting({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikethrough: document.queryCommandState('strikeThrough'),
      subscript: isSelectionSubscript(),
      code: false // Custom handling needed
    });
  }, [isEditing, canvasRef]);

  // Listen for selection changes
  useEffect(() => {
    const handleSelectionChange = () => {
      // Small delay to ensure selection is complete
      setTimeout(updateToolbarPosition, 10);
    };

    const handleMouseUp = () => {
      setTimeout(updateToolbarPosition, 10);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [updateToolbarPosition]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        blockMenuRef.current && 
        !blockMenuRef.current.contains(e.target as Node) &&
        !(e.target as Element).closest('.turn-into-btn')
      ) {
        setShowBlockMenu(false);
      }
      if (
        highlightMenuRef.current && 
        !highlightMenuRef.current.contains(e.target as Node) &&
        !(e.target as Element).closest('.highlight-btn')
      ) {
        setShowHighlightMenu(false);
      }
    };

    if (showBlockMenu || showHighlightMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBlockMenu, showHighlightMenu]);

  // Apply formatting command
  const applyFormat = useCallback((command: string) => {
    document.execCommand(command, false);
    updateToolbarPosition();
  }, [updateToolbarPosition]);

  // Apply highlight color
  const applyHighlight = useCallback((color: string) => {
    if (color === 'transparent') {
      document.execCommand('removeFormat', false);
      // Re-apply other formatting if needed
    } else {
      document.execCommand('hiliteColor', false, color);
    }
    setShowHighlightMenu(false);
    updateToolbarPosition();
  }, [updateToolbarPosition]);

  // Handle block type change
  const handleBlockTypeChange = useCallback((type: BlockType, level?: number, listStyle?: ListStyle) => {
    if (selectedBlockIndex !== null) {
      onBlockTypeChange?.(type, level, selectedBlockIndex, listStyle);
    }
    setShowBlockMenu(false);
    setPosition(prev => ({ ...prev, visible: false }));
  }, [onBlockTypeChange, selectedBlockIndex]);
  
  // Get display name for current block type
  const getCurrentTypeLabel = () => {
    if (currentBlockType === 'heading' && currentHeadingLevel) {
      return `H${currentHeadingLevel}`;
    }
    switch (currentBlockType) {
      case 'paragraph': return 'Text';
      case 'bullet_list':
        return currentListStyle === 'number' ? 'Numbered' : currentListStyle === 'letter' ? 'Lettered' : 'Bulleted';
      case 'scope_list': return 'To-do';
      default: return 'Text';
    }
  };

  if (!position.visible) return null;

  return (
    <div
      ref={toolbarRef}
      className="selection-toolbar"
      style={{ top: position.top, left: position.left }}
      onMouseDown={(e) => e.preventDefault()} // Prevent losing selection
    >
      {/* Block type selector */}
      <div className="toolbar-section">
        <button
          className="turn-into-btn"
          onClick={() => setShowBlockMenu(!showBlockMenu)}
        >
          <span className="current-type">{getCurrentTypeLabel()}</span>
          <span className="dropdown-arrow">▾</span>
        </button>

        {showBlockMenu && (
          <div ref={blockMenuRef} className="block-type-menu">
            <div className="menu-header">Turn into</div>
            {BLOCK_TYPE_OPTIONS.map((option) => {
              // Check if this option is currently active
              const isActive = option.type === currentBlockType &&
                (option.level === undefined || option.level === currentHeadingLevel) &&
                (option.listStyle === undefined || option.listStyle === currentListStyle);
              
              return (
                <button
                  key={option.label}
                  className={`menu-item ${isActive ? 'active' : ''}`}
                  onClick={() => handleBlockTypeChange(option.type, option.level, option.listStyle)}
                >
                  <span className="item-icon">{option.icon}</span>
                  <span className="item-label">{option.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Formatting buttons */}
      <div className="toolbar-section formatting">
        <button
          className={`format-btn ${formatting.bold ? 'active' : ''}`}
          onClick={() => applyFormat('bold')}
          title="Bold (⌘B)"
        >
          <strong>B</strong>
        </button>
        <button
          className={`format-btn ${formatting.italic ? 'active' : ''}`}
          onClick={() => applyFormat('italic')}
          title="Italic (⌘I)"
        >
          <em>I</em>
        </button>
        <button
          className={`format-btn ${formatting.underline ? 'active' : ''}`}
          onClick={() => applyFormat('underline')}
          title="Underline (⌘U)"
        >
          <u>U</u>
        </button>
        <button
          className={`format-btn ${formatting.strikethrough ? 'active' : ''}`}
          onClick={() => applyFormat('strikeThrough')}
          title="Strikethrough"
        >
          <s>S</s>
        </button>
        {/* Every text field, not just paragraphs: see subscript.ts. */}
        <button
          className={`format-btn ${formatting.subscript ? 'active' : ''}`}
          onClick={() => {
            toggleSubscript();
            updateToolbarPosition();
          }}
          title="Subscript (⌘,)"
        >
          <span className="subscript-icon">x<sub>2</sub></span>
        </button>
        <button
          className="format-btn"
          onClick={() => {
            const url = prompt('Enter URL:');
            if (url) {
              document.execCommand('createLink', false, url);
              updateToolbarPosition();
            }
          }}
          title="Add link (⌘K)"
        >
          🔗
        </button>
        
        {/* Highlight button with color picker */}
        <div className="highlight-container">
          <button
            className="format-btn highlight-btn"
            onClick={() => setShowHighlightMenu(!showHighlightMenu)}
            title="Highlight"
          >
            <span className="highlight-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="10" width="14" height="4" fill="#fef08a" rx="1"/>
                <path d="M4 2L8 10L12 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
          
          {showHighlightMenu && (
            <div ref={highlightMenuRef} className="highlight-menu">
              <div className="menu-header">Highlight</div>
              <div className="color-grid">
                {HIGHLIGHT_COLORS.map((item) => (
                  <button
                    key={item.name}
                    className="color-option"
                    style={{ backgroundColor: item.color === 'transparent' ? '#f5f5f5' : item.color }}
                    onClick={() => applyHighlight(item.color)}
                    title={item.name}
                  >
                    {item.color === 'transparent' && (
                      <span className="no-color">✕</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="toolbar-divider" />

      {/* Block spacing: one blank line above / below this block */}
      <div className="toolbar-section formatting">
        <button
          className={`format-btn spacing-btn ${currentSpacing.above ? 'active' : ''}`}
          onClick={() => selectedBlockIndex !== null && onSpacingToggle?.('above', selectedBlockIndex)}
          title={currentSpacing.above ? 'Space above: on (click to remove)' : 'Space above: off (click to add)'}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <line x1="2" y1="2.5" x2="14" y2="2.5" />
            <line x1="8" y1="5.5" x2="8" y2="13.5" />
            <polyline points="5,8.5 8,5.5 11,8.5" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          className={`format-btn spacing-btn ${currentSpacing.below ? 'active' : ''}`}
          onClick={() => selectedBlockIndex !== null && onSpacingToggle?.('below', selectedBlockIndex)}
          title={currentSpacing.below ? 'Space below: on (click to remove)' : 'Space below: off (click to add)'}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <line x1="2" y1="13.5" x2="14" y2="13.5" />
            <line x1="8" y1="2.5" x2="8" y2="10.5" />
            <polyline points="5,7.5 8,10.5 11,7.5" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SelectionToolbar;
